import React from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {Link as RouteLink} from 'react-router-dom'
import {Box, BoxProps, useMultiStyleConfig, Text, TextProps, Link, GridProps, SimpleGrid, AspectRatio, HStack, Image, LinkProps} from '@chakra-ui/react'
import {searchUrlBuilder, mapSearchSectionPageDesignerData, MetaTags, createRedirectUrl} from '_utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useHeaderSize, usePageDesigner} from '_customHooks'
import SearchSuggestions from '../../../header/partials/search-section/partials/suggestions'
import HelpBox from './partials/help-box'
import {EmptySearchResultsStories, LinkObject, Page, MetaTagsProps} from '_types'

interface EmptySearchResultsProps {
    searchQuery?: string | null
    searchSuggestion?: string
    isSearch: boolean
    metaProps: MetaTagsProps
}

/**
 * @name EmptySearchResults
 *
 * @component
 *
 * @description
 * Represents a page for results not found on a specified search query.
 *
 * @remarks
 * The same page can be used for both PLP and search results when there are no results found.
 *
 * @param {EmptySearchResultsProps} props - Properties object.
 * @param {string} props.searchQuery - Search query
 * @param {string} props.searchSuggestion - Search suggestion
 * @param {boolean} props.isSearch - Defines if we are in search page
 * @param {MetaTagsProps} props.metaProps - Pros used to set meta tags
 *
 * @returns {JSX.Element} - Rendered EmptySearchResults component.
 */
const EmptySearchResults: React.FC<EmptySearchResultsProps> = ({searchQuery, searchSuggestion, isSearch, metaProps}) => {
    const {headerSize} = useHeaderSize()
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('EmptySearchResults', {headerSize}) as Record<string, BoxProps & TextProps & LinkProps & GridProps>
    const {locale} = useMultiSite()

    const emptyCategoryResultsTitle = formatMessage(
        {
            id: 'label.noresults.categorySearch',
            defaultMessage: 'Sorry, no products were found for the {0} category at the moment, but they will be available again soon.',
        },
        {0: searchQuery},
    )

    const emptySearchResultsTitle = formatMessage(
        {id: 'label.noresultsfor.row.first', defaultMessage: 'Unfortunately, it seems that we couldn’t find any results for your search "{searchQuery}"'},
        {
            searchQuery: (
                <Text as="span" {...styles.searchQuery}>
                    {searchQuery}
                </Text>
            ),
        },
    )

    const {pageComponents: suggestionComponents, isLoading: isSearchSectionLoading, linkObject} = usePageDesigner({pageId: 'search-section-pwa'})
    const {ideasData, womenData, menData, productsData} = mapSearchSectionPageDesignerData(linkObject, suggestionComponents)

    const {pageComponents: storiesComponents} = usePageDesigner({pageId: 'stories-pwa'})
    const storiesData = mapStoriesPageDesignerData(linkObject, storiesComponents)

    return (
        <>
            <MetaTags {...metaProps} />

            <Box data-testid="empty-search-results" {...styles.container}>
                <Box {...styles.header}>
                    <Text as="h1" {...styles.title}>
                        {isSearch ? emptySearchResultsTitle : emptyCategoryResultsTitle}
                    </Text>

                    {isSearch &&
                        (searchSuggestion ? (
                            <Text data-testid="search-suggestion" {...styles.suggestionLabel}>
                                <FormattedMessage id="label.suggest.doyoumean" defaultMessage="Did you mean:" />{' '}
                                <Link data-testid="search-suggestion-link" as={RouteLink} to={searchUrlBuilder(searchSuggestion, locale.alias)}>
                                    {searchSuggestion}
                                </Link>
                            </Text>
                        ) : (
                            <Text {...styles.description}>
                                <FormattedMessage
                                    id="label.nosearchresults.refinement"
                                    defaultMessage="Try searching again, paying attention to any typos, or try a broader term."
                                />
                            </Text>
                        ))}
                </Box>

                <Text {...styles.suggestionsTitle}>
                    <FormattedMessage id="label.nosearchresults.suggestions" defaultMessage="You may also be interested in" />
                </Text>

                <SearchSuggestions
                    ideas={ideasData}
                    women={womenData}
                    men={menData}
                    products={productsData}
                    isPageLoading={isSearchSectionLoading}
                    isProductListing
                />

                {storiesData?.title && <Text {...styles.storiesTitle}>{storiesData?.title}</Text>}

                <SimpleGrid {...styles.grid}>
                    <Box>
                        <SimpleGrid {...styles.storiesGrid}>
                            {storiesData?.stories?.map((story, idx) => (
                                <Link href={story.href} key={`${story.alt}-${idx}`}>
                                    <AspectRatio ratio={640 / 346}>
                                        <Image alt={story.alt} title={story.alt} src={story.imageUrl} />
                                    </AspectRatio>
                                </Link>
                            ))}
                        </SimpleGrid>

                        {storiesData?.viewAll && (
                            <HStack {...styles.seeAllLinkWrapper}>
                                <Link {...styles.seeAllLink} href={storiesData?.viewAll.href} isExternal={storiesData?.viewAll.isExternal}>
                                    {storiesData?.viewAll.name}
                                </Link>
                            </HStack>
                        )}
                    </Box>

                    <HelpBox />
                </SimpleGrid>
            </Box>
        </>
    )
}

/**
 * Maps the stories page designer data for empty results page.
 *
 * @param {LinkObject} linkObject
 * @param {Page} [pageComponents] - The components of the page.
 * @returns {EmptySearchResultsStories | undefined} obj - Object containing the mapped data.
 */
const mapStoriesPageDesignerData = (linkObject: LinkObject, pageComponents?: Page): EmptySearchResultsStories | undefined => {
    const components = pageComponents?.regions?.[0]?.components

    if (components) {
        const title = components[0]?.data?.title ?? ''

        const result: EmptySearchResultsStories = {
            title: title,
            stories:
                components[0]?.regions?.[0]?.components?.slice(0, 2).map((c) => ({
                    imageUrl: c.data?.cld_image_desktop?.imageUrl,
                    href: createRedirectUrl(c.data?.image_link ?? '', linkObject.hostlink, linkObject.locale, linkObject.site),
                    alt: c.data?.cld_image_desktop?.alt,
                })) ?? [],
            viewAll: components[0]?.regions?.[0]?.components?.[2]
                ? {
                      name: components[0]?.regions?.[0]?.components?.[2]?.data?.title,
                      href: createRedirectUrl(
                          components[0]?.regions?.[0]?.components?.[2]?.data?.link,
                          linkObject.hostlink,
                          linkObject.locale,
                          linkObject.site,
                      ),
                      isExternal: components[0]?.regions?.[0]?.components?.[2]?.data?.target === '_blank',
                  }
                : undefined,
        }

        return result
    }

    return undefined
}

export default EmptySearchResults
